import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useHistory, useLocation, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUsers, faCrown, faUserTie, faHammer, faCalculator, faCog, faGavel, 
  faUserCog, faChartLine, faBuilding, faShip, faOilCan, faIndustry, 
  faUserShield, faUserGraduate, faHandshake, faTruck, faClipboard,
  faUser, faUserPlus, faSearch, faTimes, faBriefcase
} from '@fortawesome/free-solid-svg-icons';
import './HMSColleagues.css';
import './Settings.css';
import hmsAdminService from '../services/hmsAdminService';
import hmsColleaguesService from '../services/hmsColleaguesService';
import { CRMIcon } from './ui/Icons';
import { processEmployeeAvatars, getAbsoluteMediaUrl } from '../utils/mediaUtils';
import axiosInstance from '../utils/axiosConfig';
import { getErrorMessage } from '../utils/errorMessages';
import { formatBirthday } from '../utils/dateUtils';
import Footer from './ui/Footer';
import HMSEmptyState from './ui/HMSEmptyState';
import HMSPageHeader from './ui/HMSPageHeader';
import HamsterFloatingButton from './HamsterFloatingButton';
import BackToTopButton from './BackToTopButton';
import Layout from './Layout';

// Helper function to get icon for department based on name matching
// All tabs use IMT color scheme for consistency (per COLOR_REFERENCE.md)
const getDepartmentIcon = (departmentName) => {
  const lowerName = (departmentName || '').toLowerCase();
  
  if (lowerName.includes('trading') || lowerName.includes('trade')) {
    return { icon: faChartLine };
  } else if (lowerName.includes('operation')) {
    return { icon: faHammer };
  } else if (lowerName.includes('admin')) {
    return { icon: faCog };
  } else if (lowerName.includes('account') || lowerName.includes('finance')) {
    return { icon: faCalculator };
  } else if (lowerName.includes('legal')) {
    return { icon: faGavel };
  } else if (lowerName.includes('executive') || lowerName.includes('management')) {
    return { icon: faCrown };
  } else {
    return { icon: faBuilding };
  }
};

const HMSColleagues = () => {
  const history = useHistory();
  const location = useLocation();
  
  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
  
  // State management
  const [colleaguesData, setColleaguesData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('executives');
  
  // Header search state (separate from advanced filters)
  const [headerSearchQuery, setHeaderSearchQuery] = useState('');
  const [filteredTabs, setFilteredTabs] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const headerSearchDebounceRef = useRef(null);
  
  // Current user avatar state
  const [currentUserAvatar, setCurrentUserAvatar] = useState(null);
  // Search and filter states - matching admin dashboard exactly
  const [searchQuery, setSearchQuery] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [searchFilters, setSearchFilters] = useState({
    position: '',
    department: '',
    office: '',
    status: ''
  });
  const [searchResults, setSearchResults] = useState([]);
  const [hasAppliedFilters, setHasAppliedFilters] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  
  // Search debounce ref
  const searchDebounceRef = useRef(null);
  const [compatibilityReport, setCompatibilityReport] = useState(null);
  const [generatingReport, setGeneratingReport] = useState(false);
  
  // Modal states
  const [showBirthdayModal, setShowBirthdayModal] = useState(false);
  const [showAnniversaryModal, setShowAnniversaryModal] = useState(false);
  const [showCompatibilityModal, setShowCompatibilityModal] = useState(false);
  const [showEmployeeProfileModal, setShowEmployeeProfileModal] = useState(false);
  const [selectedEmployeeProfile, setSelectedEmployeeProfile] = useState(null);

  // Fetch current user avatar
  useEffect(() => {
    const fetchCurrentUserAvatar = async () => {
      try {
        const profileResponse = await axiosInstance.get('/api/profile/me/');
        if (profileResponse.data.avatar_photo) {
          setCurrentUserAvatar(profileResponse.data.avatar_photo);
        }
      } catch (error) {
        console.error('Error fetching user avatar:', error);
      }
    };
    
    fetchCurrentUserAvatar();
  }, []);

  // Lock background scroll when any modal is open
  useEffect(() => {
    const anyModalOpen = showBirthdayModal || showAnniversaryModal || showCompatibilityModal || showEmployeeProfileModal;
    document.body.style.overflow = anyModalOpen ? 'hidden' : 'auto';
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [showBirthdayModal, showAnniversaryModal, showCompatibilityModal, showEmployeeProfileModal]);

  // Load HMS Colleagues data
  const loadColleaguesData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const data = await hmsColleaguesService.getColleaguesData();
      
      // DEBUG: Log avatar data
      console.log('🖼️ HMS COLLEAGUES DEBUG: Colleagues data loaded:', data);
      if (data.employees) {
        const employeesWithAvatars = data.employees.filter(emp => emp.avatar_photo);
        console.log('🖼️ HMS COLLEAGUES DEBUG: Employees with avatars:', employeesWithAvatars.length);
        employeesWithAvatars.forEach(emp => {
          console.log(`🖼️ HMS COLLEAGUES DEBUG: ${emp.display_name} avatar: ${emp.avatar_photo}`);
        });
      }
      
      // DEBUG: Log birthday and milestone data
      if (data.birthdays) {
        console.log('🎂 BIRTHDAY DEBUG: Birthday data:', data.birthdays);
        if (data.birthdays.today && data.birthdays.today.length > 0) {
          console.log('🎂 BIRTHDAY DEBUG: Today\'s birthdays:', data.birthdays.today);
          data.birthdays.today.forEach(emp => {
            console.log(`🎂 ${emp.name} - Age: ${emp.age}, Upcoming Age: ${emp.upcoming_age}, Is Milestone: ${emp.is_milestone}`);
          });
        }
        if (data.birthdays.upcoming_this_month && data.birthdays.upcoming_this_month.length > 0) {
          console.log('🎂 BIRTHDAY DEBUG: Upcoming birthdays this month:', data.birthdays.upcoming_this_month);
          data.birthdays.upcoming_this_month.forEach(item => {
            console.log(`🎂 ${item.employee.name} - Age: ${item.employee.age}, Upcoming Age: ${item.employee.upcoming_age}, Is Milestone: ${item.employee.is_milestone}, Days: ${item.days_until}`);
          });
        }
      }
      
      setColleaguesData(data);
      
      // Set current user avatar from the response with absolute URL
      if (data.current_user?.avatar_photo) {
        const absoluteAvatarUrl = getAbsoluteMediaUrl(data.current_user.avatar_photo);
        console.log('🖼️ Current User Avatar URL:', absoluteAvatarUrl);
        setCurrentUserAvatar(absoluteAvatarUrl);
      } else {
        console.log('⚠️ No avatar photo found for current user');
      }
    } catch (err) {
      console.error('HMS Colleagues data error:', err);
      setError(getErrorMessage(err, 'Failed to load colleague information. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  // Search functionality - matching admin dashboard exactly
  const debouncedSearch = useCallback(async (query, filters) => {
    if (!query.trim() && Object.values(filters).every(v => !v)) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      // Filter employees based on search criteria - only search through active employees
      const employees = colleaguesData?.employees?.filter(emp => emp.is_active) || [];
      let filtered = employees;

      // Apply search query
      if (query.trim()) {
        const searchLower = query.toLowerCase();
        filtered = filtered.filter(emp =>
          emp.first_name?.toLowerCase().includes(searchLower) ||
          emp.last_name?.toLowerCase().includes(searchLower) ||
          emp.display_name?.toLowerCase().includes(searchLower) ||
          emp.hms_employee_id?.toLowerCase().includes(searchLower) ||
          emp.position?.name?.toLowerCase().includes(searchLower) ||
          emp.department?.name?.toLowerCase().includes(searchLower) ||
          emp.office?.name?.toLowerCase().includes(searchLower) ||
          emp.email?.toLowerCase().includes(searchLower)
        );
      }

      // Apply filters
      if (filters.position) {
        filtered = filtered.filter(emp => emp.position?.name === filters.position || emp.position?.code === filters.position);
      }
      if (filters.department) {
        filtered = filtered.filter(emp => emp.department?.name === filters.department);
      }
      if (filters.office) {
        filtered = filtered.filter(emp => emp.office?.name === filters.office);
      }
      if (filters.status) {
        const isActive = filters.status === 'true';
        filtered = filtered.filter(emp => emp.is_active === isActive);
      }

      setSearchResults(filtered);
    } catch (err) {
      console.error('Search error:', err);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [colleaguesData]);

  // Handle search input change with debouncing - matching admin dashboard
  const handleSearchInputChange = useCallback((e) => {
    const value = e.target.value;
    setSearchQuery(value);

    // Clear previous debounce
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    // Set new debounce
    searchDebounceRef.current = setTimeout(() => {
      if (value.trim()) {
        debouncedSearch(value, searchFilters);
      } else {
        setSearchResults([]);
      }
    }, 500); // 500ms debounce
  }, [debouncedSearch, searchFilters]);

  // Handle manual search (form submission) - matching admin dashboard
  const handleSearch = async (e) => {
    e.preventDefault();

    // Clear debounce
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    await debouncedSearch(searchQuery, searchFilters);
  };

  // Handle filter changes - matching admin dashboard
  const handleFilterChange = (field, value) => {
    const newFilters = { ...searchFilters, [field]: value };
    setSearchFilters(newFilters);
    setHasAppliedFilters(false);
  };

  // Apply filters - matching admin dashboard
  const applyFilters = async () => {
    try {
      setIsSearching(true);
      await debouncedSearch(searchQuery, searchFilters);
      setHasAppliedFilters(true);
    } finally {
      setIsSearching(false);
    }
  };

  // Clear search results - matching admin dashboard
  const clearSearchResults = () => {
    setSearchResults([]);
    setSearchQuery('');
  };

  // Generate compatibility report
  const generateCompatibilityReport = async () => {
    try {
      setGeneratingReport(true);
      
      const data = await hmsColleaguesService.generateCompatibilityReport();
      setCompatibilityReport(data);
    } catch (err) {
      console.error('Compatibility report error:', err);
      setError(getErrorMessage(err, 'Failed to load colleague information. Please try again.'));
    } finally {
      setGeneratingReport(false);
    }
  };

  // Header search - filters tabs and employees by name or department
  const handleHeaderSearch = useCallback((query) => {
    if (!query.trim()) {
      setFilteredTabs([]);
      setFilteredEmployees([]);
      return;
    }

    const searchLower = query.toLowerCase();
    const allEmployees = colleaguesData?.employees?.filter(emp => emp.is_active) || [];
    
    // Filter employees by name
    const matchedByName = allEmployees.filter(emp =>
      emp.first_name?.toLowerCase().includes(searchLower) ||
      emp.last_name?.toLowerCase().includes(searchLower) ||
      emp.display_name?.toLowerCase().includes(searchLower)
    );
    
    // Filter employees by department name
    const matchedByDepartment = allEmployees.filter(emp =>
      emp.department?.name?.toLowerCase().includes(searchLower)
    );
    
    // Combine and deduplicate
    const allMatches = [...new Set([...matchedByName, ...matchedByDepartment])];
    
    setFilteredEmployees(allMatches);
    
    // Determine which tabs should be shown
    const tabsToShow = new Set();
    
    allMatches.forEach(emp => {
      // Check if employee is executive
      if (emp.position?.code === 'ceo' || emp.position?.code === 'cfo') {
        tabsToShow.add('executives');
      }
      
      // Add department tab
      if (emp.department?.id) {
        tabsToShow.add(`dept_${emp.department.id}`);
      } else {
        tabsToShow.add('unassigned');
      }
    });
    
    setFilteredTabs(Array.from(tabsToShow));
    
    // If only one tab matches, auto-select it
    if (tabsToShow.size === 1) {
      setActiveTab(Array.from(tabsToShow)[0]);
    }
  }, [colleaguesData]);

  // Header search input handler with debounce
  const handleHeaderSearchInput = useCallback((e) => {
    const value = e.target.value;
    setHeaderSearchQuery(value);

    // Clear previous debounce
    if (headerSearchDebounceRef.current) {
      clearTimeout(headerSearchDebounceRef.current);
    }

    // Set new debounce
    headerSearchDebounceRef.current = setTimeout(() => {
      handleHeaderSearch(value);
    }, 500); // 500ms debounce
  }, [handleHeaderSearch]);

  // Clear header search
  const clearHeaderSearch = () => {
    setHeaderSearchQuery('');
    setFilteredTabs([]);
    setFilteredEmployees([]);
  };

  // Handle view employee profile
  const handleViewEmployeeProfile = (employee) => {
    setSelectedEmployeeProfile(employee);
    setShowEmployeeProfileModal(true);
  };

  // Helper function to format languages display
  const formatLanguages = (languages) => {
    if (!languages || !Array.isArray(languages) || languages.length === 0) return 'Not set';
    
    return languages.map(lang => {
      if (typeof lang !== 'string') return '';
      // Convert underscore to space and capitalize each word
      return lang.split('_').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      ).join(' ');
    }).filter(Boolean).join(', ') || 'Not set';
  };

  // Helper function to format working characteristics
  const formatCharacteristics = (characteristics) => {
    if (!characteristics || !Array.isArray(characteristics) || characteristics.length === 0) return 'Not set';
    
    return characteristics.map(char => {
      if (typeof char !== 'string') return '';
      // Convert underscore to space and capitalize each word
      return char.split('_').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      ).join(' ');
    }).filter(Boolean).join(', ') || 'Not set';
  };

  // Determine which employees to display - filter out inactive employees for main display
  const allEmployees = colleaguesData?.employees || [];
  const activeEmployees = allEmployees.filter(emp => emp.is_active);
  
  const displayEmployees = searchResults.length > 0 || hasAppliedFilters || searchQuery.trim() 
    ? searchResults 
    : activeEmployees;

  // Load data on component mount
  useEffect(() => {
    loadColleaguesData();
  }, []);


  if (loading) {
    return (
      <Layout showLeftNavSidebar={true} showRightSidebar={false}>
        <div className="account-information-layout">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading Colleagues...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout showLeftNavSidebar={true} showRightSidebar={false}>
        <div className="settings-page">
          <div className="settings-container">
            <div className="error-state">
              <div className="error-icon">⚠</div>
              <h2>Unable to load Colleagues</h2>
              <p>{error}</p>
              <button onClick={loadColleaguesData} className="retry-button">
                Retry
              </button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // Get executives and department groups from backend
  const executives = colleaguesData?.executives || [];
  const departmentGroups = colleaguesData?.department_groups || {};
  const departmentOrder = colleaguesData?.department_order || [];
  
  // Get processed employees for consistent avatar URLs
  const processedEmployees = colleaguesData?.employees || [];
  
  // Create a map of processed employees by ID for quick lookup
  const processedEmployeesMap = processedEmployees.reduce((map, emp) => {
    map[emp.id] = emp;
    return map;
  }, {});
  
  // Replace employees in department groups with processed versions (for consistent avatar URLs)
  const processedDepartmentGroups = { ...departmentGroups };
  Object.keys(processedDepartmentGroups).forEach(deptKey => {
    if (processedDepartmentGroups[deptKey].employees) {
      processedDepartmentGroups[deptKey].employees = processedDepartmentGroups[deptKey].employees.map(emp => 
        processedEmployeesMap[emp.id] || emp
      );
    }
  });
  
  // Get available tabs based on data
  const getAvailableTabs = () => {
    const tabs = [];
    
    // If header search is active, only show filtered tabs
    const tabsToDisplay = filteredTabs.length > 0 
      ? filteredTabs 
      : null; // null means show all
    
    // Always add executives tab first if we have executives AND it matches filter (or no filter)
    if (executives.length > 0 && (!tabsToDisplay || tabsToDisplay.includes('executives'))) {
      tabs.push({
        value: 'executives',
        label: 'Executive Leadership',
        icon: faCrown,
        isSpecial: true
      });
    }
    
    // Add department tabs that have data AND match filter (or no filter)
    departmentOrder.forEach(deptKey => {
      if (tabsToDisplay && !tabsToDisplay.includes(deptKey)) {
        return; // Skip tabs not in filter
      }
      
      const group = processedDepartmentGroups[deptKey];
      if (group && group.employees && group.employees.length > 0) {
        const iconConfig = getDepartmentIcon(group.department_name);
        tabs.push({
          value: deptKey,
          label: group.display_name,
          icon: iconConfig.icon,
          departmentId: group.department_id,
          departmentName: group.department_name
        });
      }
    });
    
    return tabs;
  };
  
  const availableTabs = getAvailableTabs();
  
  // Check if a department has employees with birthdays today
  const departmentHasBirthday = (tabValue) => {
    if (tabValue === 'executives') {
      return executives.some(emp => emp.is_birthday_today);
    } else {
      const group = processedDepartmentGroups[tabValue];
      return group && group.employees && group.employees.some(emp => emp.is_birthday_today);
    }
  };

  // Tab tray for the shared page header. Headcounts render as the pill's
  // corner badge (executives count their own list, departments use the
  // group count), and the birthday glow rides the pill as a variant class.
  const colleaguesTabs = availableTabs.map(tab => ({
    key: tab.value,
    label: tab.label,
    icon: tab.icon,
    count: tab.value === 'executives'
      ? executives.length
      : processedDepartmentGroups[tab.value]?.count,
    showZero: true,
    className: departmentHasBirthday(tab.value) ? 'has-birthday' : undefined
  }));

  // Render content for the active tab
  const renderTabContent = () => {
    if (activeTab === 'executives') {
      // Filter executives if header search active
      let executivesToShow = executives;
      if (headerSearchQuery.trim() && filteredEmployees.length > 0) {
        const filteredIds = new Set(filteredEmployees.map(e => e.id));
        executivesToShow = executives.filter(exec => filteredIds.has(exec.id));
      }
      
      return (
        <div className="executive-leadership-section">
          <div className="executive-frame">
            <div className="executive-frame-header">
              <FontAwesomeIcon icon={faCrown} className="executive-frame-icon" />
              <h3 className="executive-frame-title">Executive Leadership</h3>
            </div>
            <div className="executive-grid">
              {executivesToShow.map(exec => (
                <div 
                  key={exec.id} 
                  className={`executive-card ${exec.is_birthday_today ? 'birthday-today' : ''}`}
                  onClick={() => handleViewEmployeeProfile(exec)}
                >
                  <div className="executive-avatar-container">
                    {exec.avatar_photo ? (
                    <img 
                      src={exec.avatar_photo} 
                      alt={exec.get_display_name || exec.display_name}
                      className="executive-avatar"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div 
                      className="executive-avatar-initials"
                      style={{ display: exec.avatar_photo ? 'none' : 'flex' }}
                    >
                      {exec.first_name?.[0] || exec.email?.[0] || '?'}{exec.last_name?.[0] || ''}
                    </div>
                  </div>
                  
                  <div className="executive-info">
                    <h3 className="executive-name">{exec.get_display_name || exec.display_name}</h3>
                    <p className="executive-position">{exec.position?.name || 'Executive'}</p>
                    {(exec.department?.name || exec.office?.name) && (
                      <p className="executive-location">{[exec.department?.name, exec.office?.name].filter(Boolean).join(' / ')}</p>
                    )}
                  </div>
                  <div className="executive-pills">
                    {exec.date_of_birth && (
                      <span className="exec-pill">
                        <CRMIcon name="birthday-cake" />
                        {formatBirthday(exec.date_of_birth)}
                      </span>
                    )}
                    {exec.years_at_hms != null && exec.years_at_hms >= 0 && (
                      <span className="exec-pill">
                        <FontAwesomeIcon icon={faBriefcase} />
                        {exec.years_at_hms} {exec.years_at_hms === 1 ? 'yr' : 'yrs'}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    } else {
      // Render department group content
      const group = processedDepartmentGroups[activeTab];
      if (!group || !group.employees || group.employees.length === 0) {
        return (
          <HMSEmptyState
            variant="card"
            title="No employees found in this department."
          />
        );
      }
      
      // Filter department employees if header search active
      let employeesToShow = group.employees;
      if (headerSearchQuery.trim() && filteredEmployees.length > 0) {
        const filteredIds = new Set(filteredEmployees.map(e => e.id));
        employeesToShow = group.employees.filter(emp => filteredIds.has(emp.id));
      }
      
      return (
        <div className="department-group-content">
          <div className="employee-grid">
            {employeesToShow.map(emp => (
              <div 
                key={emp.id} 
                className={`employee-card ${emp.is_birthday_today ? 'birthday-today' : ''}`}
                onClick={() => handleViewEmployeeProfile(emp)}
                style={{ cursor: 'pointer' }}
              >
                <div className="employee-avatar-container">
                  {emp.avatar_photo ? (
                    <img 
                      src={emp.avatar_photo} 
                      alt={emp.get_display_name || emp.display_name}
                      className="employee-avatar"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div 
                    className="employee-avatar-initials"
                    style={{ display: emp.avatar_photo ? 'none' : 'flex' }}
                  >
                    {emp.first_name?.[0] || emp.email?.[0] || '?'}{emp.last_name?.[0] || ''}
                  </div>
                </div>
                
                <div className="employee-info">
                  <h4 className="employee-name">{emp.get_display_name || emp.display_name}</h4>
                  <p className="employee-position">{emp.position?.name || 'Employee'}</p>
                  <p className="employee-department">{emp.department?.name || 'No Department'}</p>
                  <p className="employee-office">{emp.office?.name || 'No Office'}</p>
                </div>
                <div className="card-meta">
                  {emp.date_of_birth && (
                    <span className="meta-item meta-birthday">
                      <CRMIcon name="birthday-cake" />
                      {formatBirthday(emp.date_of_birth)}
                    </span>
                  )}
                  {emp.years_at_hms != null && emp.years_at_hms >= 0 && (
                    <span className="meta-item meta-tenure">
                      <FontAwesomeIcon icon={faBriefcase} />
                      {emp.years_at_hms} {emp.years_at_hms === 1 ? 'year' : 'years'}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }
  };

  return (
    <>
      {/* Modals at viewport level */}
      {showBirthdayModal && (
        <div className="modal-overlay" onClick={() => setShowBirthdayModal(false)}>
          <div className="modal-content birthday-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <CRMIcon name="birthday-cake" className="modal-icon" />
                Birthday Central
              </h2>
              <button 
                className="modal-close"
                onClick={() => setShowBirthdayModal(false)}
              >
                <CRMIcon name="close" />
              </button>
            </div>
            <div className="modal-body">
              {/* Today's Birthday Banner */}
              {colleaguesData?.birthdays?.today?.length > 0 && (
                <div className="birthday-celebration-banner">
                  <div className="birthday-content">
                    <div className="birthday-message">
                      <h3>Happy Birthday!</h3>
                      <div className="birthday-employees">
                        {colleaguesData.birthdays.today.map(emp => (
                          <div key={emp.id} className="birthday-employee">
                            <div className="birthday-avatar">
                              {emp.avatar ? (
                                <img src={getAbsoluteMediaUrl(emp.avatar)} alt={emp.name} />
                              ) : (
                                <div className="avatar-initials">
                                  {emp.name.split(' ').map(n => n[0]).join('')}
                                </div>
                              )}
                              {emp.is_milestone && (
                                <div className="milestone-badge">
                                  <CRMIcon name="star" />
                                </div>
                              )}
                            </div>
                            <div className="birthday-employee-details">
                              <span className="birthday-name">{emp.name}</span>
                              <span className="birthday-position">{emp.position || 'Employee'}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Birthday Stats */}
              <div className="birthday-stats-grid">
                <div className="birthday-stat-card">
                  <div className="stat-icon">
                    <CRMIcon name="calendar-day" size="2x" />
                  </div>
                  <div className="stat-info">
                    <span className="stat-number">{colleaguesData?.birthdays?.today?.length || 0}</span>
                    <span className="stat-label">Birthday{colleaguesData?.birthdays?.today?.length !== 1 ? 's' : ''} Today</span>
                  </div>
                </div>
                
                <div className="birthday-stat-card">
                  <div className="stat-icon">
                    <CRMIcon name="calendar-alt" size="2x" />
                  </div>
                  <div className="stat-info">
                    <span className="stat-number">{colleaguesData?.birthdays?.upcoming_this_month?.length || 0}</span>
                    <span className="stat-label">Upcoming This Month</span>
                  </div>
                </div>
                
                <div className="birthday-stat-card">
                  <div className="stat-icon">
                    <CRMIcon name="clock" size="2x" />
                  </div>
                  <div className="stat-info">
                    <span className="stat-number">
                      {colleaguesData?.birthdays?.upcoming_this_month?.length > 0 
                        ? Math.min(...colleaguesData.birthdays.upcoming_this_month.map(b => b.days_until))
                        : colleaguesData?.birthdays?.upcoming?.length > 0 
                          ? Math.min(...colleaguesData.birthdays.upcoming.map(b => b.days_until))
                          : '---'}
                    </span>
                    <span className="stat-label">Days to Next</span>
                  </div>
                </div>
              </div>

              {/* Upcoming Birthdays List */}
              {colleaguesData?.birthdays?.upcoming_this_month?.length > 0 ? (
                <div className="upcoming-birthdays-container">
                  <h4>Next Upcoming Birthdays</h4>
                  <div className="upcoming-birthdays-list">
                    {colleaguesData.birthdays.upcoming_this_month.map(item => (
                      <div key={item.employee.id} className={`upcoming-birthday-item ${item.employee.is_milestone ? 'milestone-birthday' : ''}`}>
                        <div className="upcoming-avatar">
                          {item.employee.avatar ? (
                            <img 
                              src={getAbsoluteMediaUrl(item.employee.avatar)} 
                              alt={item.employee.name}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div 
                            className="avatar-initials"
                            style={{ display: item.employee.avatar ? 'none' : 'flex' }}
                          >
                            {item.employee.name.split(' ').map(n => n[0]).join('')}
                          </div>
                          {item.employee.is_milestone && (
                            <div className="milestone-badge-small">
                              <CRMIcon name="star" />
                            </div>
                          )}
                        </div>
                        <div className="upcoming-info">
                          <span className="upcoming-name">{item.employee.name}</span>
                          <span className="upcoming-position">{item.employee.position || 'Employee'}</span>
                          <span className="upcoming-countdown">
                            {item.days_until} day{item.days_until !== 1 ? 's' : ''} until birthday
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <HMSEmptyState
                  icon={<CRMIcon name="gift" size="1x" />}
                  title="No upcoming birthdays this month"
                  hint="Check back next month for upcoming celebrations!"
                />
              )}
            </div>
          </div>
        </div>
      )}

      {showAnniversaryModal && (
        <div className="modal-overlay" onClick={() => setShowAnniversaryModal(false)}>
          <div className="modal-content anniversary-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <FontAwesomeIcon icon={faBriefcase} className="modal-icon" />
                Anniversary Central
              </h2>
              <button 
                className="modal-close"
                onClick={() => setShowAnniversaryModal(false)}
              >
                <CRMIcon name="close" />
              </button>
            </div>
            <div className="modal-body">
              {/* Today's Anniversary Banner */}
              {colleaguesData?.anniversaries?.today?.length > 0 && (
                <div className="anniversary-celebration-banner">
                  <div className="anniversary-content">
                    <div className="anniversary-message">
                      <h3>Happy Work Anniversary!</h3>
                      <div className="anniversary-employees">
                        {colleaguesData.anniversaries.today.map(emp => (
                          <div key={emp.id} className="anniversary-employee">
                            <div className="anniversary-avatar">
                              {emp.avatar ? (
                                <img src={getAbsoluteMediaUrl(emp.avatar)} alt={emp.name} />
                              ) : (
                                <div className="avatar-initials">
                                  {emp.name.split(' ').map(n => n[0]).join('')}
                                </div>
                              )}
                            </div>
                            <div className="anniversary-details">
                              <span className="anniversary-name">{emp.name}</span>
                              <span className="anniversary-tenure">{emp.years_at_hms} {emp.years_at_hms === 1 ? 'year' : 'years'} at IMT</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Anniversary Stats */}
              <div className="anniversary-stats-grid">
                <div className="anniversary-stat-card">
                  <div className="stat-icon">
                    <CRMIcon name="calendar-day" size="2x" />
                  </div>
                  <div className="stat-info">
                    <span className="stat-number">{colleaguesData?.anniversaries?.today?.length || 0}</span>
                    <span className="stat-label">Anniversary{colleaguesData?.anniversaries?.today?.length !== 1 ? 's' : ''} Today</span>
                  </div>
                </div>
                
                <div className="anniversary-stat-card">
                  <div className="stat-icon">
                    <CRMIcon name="calendar-alt" size="2x" />
                  </div>
                  <div className="stat-info">
                    <span className="stat-number">{colleaguesData?.anniversaries?.upcoming_this_month?.length || 0}</span>
                    <span className="stat-label">Upcoming This Month</span>
                  </div>
                </div>
                
                <div className="anniversary-stat-card">
                  <div className="stat-icon">
                    <CRMIcon name="clock" size="2x" />
                  </div>
                  <div className="stat-info">
                    <span className="stat-number">
                      {colleaguesData?.anniversaries?.upcoming_this_month?.length > 0 
                        ? Math.min(...colleaguesData.anniversaries.upcoming_this_month.map(a => a.days_until))
                        : colleaguesData?.anniversaries?.upcoming?.length > 0 
                          ? Math.min(...colleaguesData.anniversaries.upcoming.map(a => a.days_until))
                          : '---'}
                    </span>
                    <span className="stat-label">Days to Next</span>
                  </div>
                </div>
              </div>

              {/* Upcoming Anniversaries List - Sorted by longest tenure (years_at_hms descending) */}
              {colleaguesData?.anniversaries?.upcoming_this_month?.length > 0 ? (
                <div className="upcoming-anniversaries-container">
                  <h4>Next Upcoming Anniversaries</h4>
                  <div className="upcoming-anniversaries-list">
                    {[...colleaguesData.anniversaries.upcoming_this_month]
                      .sort((a, b) => (b.years_at_hms || 0) - (a.years_at_hms || 0))
                      .map(item => (
                      <div key={item.employee.id} className="upcoming-anniversary-item">
                        <div className="anniversary-years-badge-container">
                          <span className="anniversary-years-number">{item.years_at_hms ?? '?'}</span>
                          <span className="anniversary-years-label">{item.years_at_hms === 1 ? 'year' : 'years'}</span>
                        </div>
                        <div className="upcoming-avatar">
                          {item.employee.avatar ? (
                            <img 
                              src={getAbsoluteMediaUrl(item.employee.avatar)} 
                              alt={item.employee.name}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div 
                            className="avatar-initials"
                            style={{ display: item.employee.avatar ? 'none' : 'flex' }}
                          >
                            {item.employee.name.split(' ').map(n => n[0]).join('')}
                          </div>
                        </div>
                        <div className="upcoming-info">
                          <span className="upcoming-name">{item.employee.name}</span>
                          <span className="upcoming-position">{item.employee.position || 'Employee'} -- {item.employee.department || 'IMT'}</span>
                          <span className="upcoming-join-date">
                            Joined {item.employee.date_joined ? new Date(item.employee.date_joined).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}
                          </span>
                          <span className="upcoming-countdown">
                            {item.days_until} day{item.days_until !== 1 ? 's' : ''} until anniversary
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <HMSEmptyState
                  icon={<FontAwesomeIcon icon={faBriefcase} />}
                  title="No upcoming anniversaries this month"
                  hint="Check back next month for upcoming celebrations!"
                />
              )}
            </div>
          </div>
        </div>
      )}

      {showCompatibilityModal && (
        <div className="modal-overlay" onClick={() => setShowCompatibilityModal(false)}>
          <div className="modal-content compatibility-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <CRMIcon name="handshake" className="modal-icon" />
                Work Style Compatibility
              </h2>
              <button 
                className="modal-close"
                onClick={() => setShowCompatibilityModal(false)}
              >
                <CRMIcon name="close" />
              </button>
            </div>
            <div className="modal-body">
              <div className="compatibility-intro-section">
                <p className="compatibility-intro-text">Our AI analyzes your working style, preferences, and collaboration approach to identify teammates who complement your work ethic and share similar professional values.</p>
                <div className="compatibility-controls">
                  <button 
                    onClick={generateCompatibilityReport}
                    disabled={generatingReport}
                    className={`generate-report-btn ${generatingReport ? 'analyzing' : ''}`}
                  >
                    {generatingReport ? (
                      <>
                        <FontAwesomeIcon icon={faCog} spin /> Analyzing...
                      </>
                    ) : (
                      <>
                        <CRMIcon name="search" /> Find Your Likeminded Team
                      </>
                    )}
                  </button>
                </div>
              </div>

              {compatibilityReport && (
                <div className="compatibility-results">
                  {compatibilityReport.compatible_colleagues && compatibilityReport.compatible_colleagues.length > 0 ? (
                    <div className="likeminded-team-section">
                      <h3 className="likeminded-title">Your Likeminded Teammates</h3>
                      
                      <div className="compatible-colleagues-list">
                        {compatibilityReport.compatible_colleagues.map((colleague) => (
                          <div key={colleague.hms_id} className="colleague-card">
                            <div className="colleague-avatar">
                              {colleague.avatar ? (
                                <img 
                                  src={colleague.avatar} 
                                  alt={colleague.name}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                              ) : null}
                              <div 
                                className="colleague-avatar-initials"
                                style={{ display: colleague.avatar ? 'none' : 'flex' }}
                              >
                                {colleague.name.split(' ').map(n => n[0]).join('')}
                              </div>
                            </div>
                            <div className="colleague-info">
                              <h4 className="colleague-name">{colleague.name}</h4>
                              <p className="colleague-position">{colleague.position}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      <div className="compatibility-explanation">
                        <p>{compatibilityReport.explanation}</p>
                      </div>
                    </div>
                  ) : (
                    <HMSEmptyState
                      icon={<FontAwesomeIcon icon={faUsers} />}
                      title="No Compatible Colleagues Found"
                      hint={compatibilityReport.explanation || 'Complete your working style information in your account settings to find compatible colleagues.'}
                      action={compatibilityReport.redirect_to_account && (
                        <button
                          className="account-info-btn"
                          onClick={() => {
                            setShowCompatibilityModal(false);
                            window.location.href = '/settings/account-information';
                          }}
                        >
                          Update Account Information
                        </button>
                      )}
                    />
                  )}
                  
                  {compatibilityReport.total_analyzed > 0 && (
                    <div className="analysis-stats">
                      <span>Analyzed {compatibilityReport.total_analyzed} colleague{compatibilityReport.total_analyzed !== 1 ? 's' : ''}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Employee Profile Modal */}
      {showEmployeeProfileModal && selectedEmployeeProfile && (
        <div className="modal-overlay" onClick={() => setShowEmployeeProfileModal(false)}>
          <div className="modal-content employee-profile-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <CRMIcon name="user" className="modal-icon" />
                Employee Profile
              </h2>
              <button 
                className="modal-close"
                onClick={() => setShowEmployeeProfileModal(false)}
              >
                <CRMIcon name="close" />
              </button>
            </div>
            <div className="modal-body">
              <div className="employee-details-content">
                {/* Employee Header */}
                <div className="employee-header">
                  <div className="employee-avatar">
                    {selectedEmployeeProfile.avatar_photo ? (
                      <img src={selectedEmployeeProfile.avatar_photo} alt="Employee Avatar" />
                    ) : (
                      <div className="avatar-placeholder">
                        {selectedEmployeeProfile.first_name?.[0] || selectedEmployeeProfile.email?.[0] || '?'}{selectedEmployeeProfile.last_name?.[0] || ''}
                      </div>
                    )}
                  </div>
                  <div className="employee-basic-info">
                    <h3>{selectedEmployeeProfile.display_name}</h3>
                    <p className="employee-title">{selectedEmployeeProfile.position?.name || 'Position not set'}</p>
                    <div className="employee-status">
                      <span className={`status-badge ${selectedEmployeeProfile.is_active ? 'active' : 'inactive'}`}>
                        {selectedEmployeeProfile.is_active ? 'Active' : 'Inactive'}
                      </span>
                      {selectedEmployeeProfile.is_superuser && (
                        <span className="status-badge superuser">Administrator</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Celebrations Context Bar */}
                <div className="celebrations-bar">
                  <div className="celebration-item">
                    <CRMIcon name="birthday-cake" className="celebration-icon" />
                    <div className="celebration-details">
                      <span className="celebration-label">Birthday</span>
                      <span className="celebration-value">{formatBirthday(selectedEmployeeProfile.date_of_birth)}</span>
                      {selectedEmployeeProfile.days_until_birthday != null && selectedEmployeeProfile.days_until_birthday > 0 && (
                        <span className="celebration-countdown">{selectedEmployeeProfile.days_until_birthday} day{selectedEmployeeProfile.days_until_birthday !== 1 ? 's' : ''} away</span>
                      )}
                      {selectedEmployeeProfile.is_birthday_today && (
                        <span className="celebration-today">Today!</span>
                      )}
                    </div>
                  </div>
                  <div className="celebration-divider"></div>
                  <div className="celebration-item">
                    <FontAwesomeIcon icon={faBriefcase} className="celebration-icon" />
                    <div className="celebration-details">
                      <span className="celebration-label">IMT Tenure</span>
                      <span className="celebration-value">
                        {selectedEmployeeProfile.years_at_hms != null 
                          ? `${selectedEmployeeProfile.years_at_hms} ${selectedEmployeeProfile.years_at_hms === 1 ? 'year' : 'years'}`
                          : 'Not set'}
                      </span>
                      {selectedEmployeeProfile.days_until_anniversary != null && selectedEmployeeProfile.days_until_anniversary > 0 && (
                        <span className="celebration-countdown">Anniversary in {selectedEmployeeProfile.days_until_anniversary} day{selectedEmployeeProfile.days_until_anniversary !== 1 ? 's' : ''}</span>
                      )}
                      {selectedEmployeeProfile.is_anniversary_today && (
                        <span className="celebration-today">Anniversary today!</span>
                      )}
                    </div>
                  </div>
                  <div className="celebration-divider"></div>
                  <div className="celebration-item">
                    <CRMIcon name="id-badge" className="celebration-icon" />
                    <div className="celebration-details">
                      <span className="celebration-label">IMT ID</span>
                      <span className="celebration-value">{selectedEmployeeProfile.hms_employee_id || 'Not assigned'}</span>
                    </div>
                  </div>
                </div>

                {/* Employee Details Grid */}
                <div className="employee-details-grid">
                  {/* Basic Information */}
                  <div className="details-section">
                    <h4>Basic Information</h4>
                    <div className="details-row">
                      <span className="label">Email:</span>
                      <span className="value">{selectedEmployeeProfile.email}</span>
                    </div>
                    <div className="details-row">
                      <span className="label">First Name:</span>
                      <span className="value">{selectedEmployeeProfile.first_name || 'Not set'}</span>
                    </div>
                    <div className="details-row">
                      <span className="label">Last Name:</span>
                      <span className="value">{selectedEmployeeProfile.last_name || 'Not set'}</span>
                    </div>
                    <div className="details-row">
                      <span className="label">Preferred Name:</span>
                      <span className="value">{selectedEmployeeProfile.preferred_name || 'Not set'}</span>
                    </div>
                    <div className="details-row">
                      <span className="label">Short Name:</span>
                      <span className="value">{selectedEmployeeProfile.short_name || 'Not set'}</span>
                    </div>
                    <div className="details-row">
                      <span className="label">Birthday:</span>
                      <span className="value">{formatBirthday(selectedEmployeeProfile.date_of_birth)}</span>
                    </div>
                    <div className="details-row">
                      <span className="label">Joined IMT:</span>
                      <span className="value">
                        {selectedEmployeeProfile.date_joined_hms 
                          ? new Date(selectedEmployeeProfile.date_joined_hms).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                          : 'Not set'}
                      </span>
                    </div>
                  </div>

                  {/* Business Context */}
                  <div className="details-section">
                    <h4>Business Context</h4>
                    <div className="details-row">
                      <span className="label">Solid Fuels:</span>
                      <span className="value">{selectedEmployeeProfile.business_solid_fuels ? 'Yes' : 'No'}</span>
                    </div>
                    <div className="details-row">
                      <span className="label">Liquid Fuels:</span>
                      <span className="value">{selectedEmployeeProfile.business_liquid_fuels ? 'Yes' : 'No'}</span>
                    </div>
                  </div>

                  {/* Organization */}
                  <div className="details-section">
                    <h4>Organization</h4>
                    <div className="details-row">
                      <span className="label">Office/Legal Entity:</span>
                      <span className="value">
                        {selectedEmployeeProfile.office?.name || 
                         (typeof selectedEmployeeProfile.office === 'string' ? selectedEmployeeProfile.office : 'Not set')}
                      </span>
                    </div>
                    <div className="details-row">
                      <span className="label">Department:</span>
                      <span className="value">
                        {selectedEmployeeProfile.department?.name || 
                         (typeof selectedEmployeeProfile.department === 'string' ? selectedEmployeeProfile.department : 'Not set')}
                      </span>
                    </div>
                    <div className="details-row">
                      <span className="label">Position:</span>
                      <span className="value">{selectedEmployeeProfile.position?.name || 'Not set'}</span>
                    </div>
                    <div className="details-row">
                      <span className="label">Location:</span>
                      <span className="value">{selectedEmployeeProfile.location || 'Not set'}</span>
                    </div>
                    <div className="details-row">
                      <span className="label">Supervisors:</span>
                      <span className="value">
                        {selectedEmployeeProfile.supervisors && selectedEmployeeProfile.supervisors.length > 0 ? (
                          selectedEmployeeProfile.supervisors.map((sup, idx) => (
                            <span key={sup.id}>
                              {sup.name}
                              {idx < selectedEmployeeProfile.supervisors.length - 1 ? ', ' : ''}
                            </span>
                          ))
                        ) : 'Not set'}
                      </span>
                    </div>
                    <div className="details-row">
                      <span className="label">Heads of Department:</span>
                      <span className="value">
                        {selectedEmployeeProfile.heads_of_department && selectedEmployeeProfile.heads_of_department.length > 0 ? (
                          selectedEmployeeProfile.heads_of_department.map((hod, idx) => (
                            <span key={hod.id}>
                              {hod.name}
                              {idx < selectedEmployeeProfile.heads_of_department.length - 1 ? ', ' : ''}
                            </span>
                          ))
                        ) : 'Not set'}
                      </span>
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div className="details-section">
                    <h4>Contact Information</h4>
                    <div className="details-row">
                      <span className="label">Work Phone:</span>
                      <span className="value">
                        {selectedEmployeeProfile.work_phone ? (
                          selectedEmployeeProfile.work_phone.startsWith('+') 
                            ? selectedEmployeeProfile.work_phone 
                            : `+27${selectedEmployeeProfile.work_phone.startsWith('0') ? selectedEmployeeProfile.work_phone.substring(1) : selectedEmployeeProfile.work_phone}`
                        ) : 'Not set'}
                      </span>
                    </div>
                    <div className="details-row">
                      <span className="label">Work Email:</span>
                      <span className="value">{selectedEmployeeProfile.work_email || selectedEmployeeProfile.email || 'Not set'}</span>
                    </div>
                  </div>

                  {/* Languages */}
                  <div className="details-section">
                    <h4>Languages Spoken</h4>
                    <div className="details-row">
                      <span className="label">Languages:</span>
                      <span className="value">
                        {formatLanguages(selectedEmployeeProfile.languages_spoken)}
                      </span>
                    </div>
                  </div>

                  {/* Working With Me Guide */}
                  <div className="details-section">
                    <h4>Working With Me Guide</h4>
                    <div className="details-row">
                      <span className="label">Characteristics:</span>
                      <span className="value">
                        {formatCharacteristics(selectedEmployeeProfile.working_characteristics)}
                      </span>
                    </div>
                    {selectedEmployeeProfile.working_guide_custom && (
                      <div className="details-row">
                        <span className="label">Custom Guide:</span>
                        <span className="value working-guide-custom">{selectedEmployeeProfile.working_guide_custom}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content wrapped in Layout */}
      <Layout showLeftNavSidebar={true} showRightSidebar={false}>
        <div className="hms-colleagues-module">
        {/* Shared screen header: brand block, pill tray, header widgets */}
        <HMSPageHeader
          title="IMT Colleagues"
          kicker="Workspace"
          tabs={{ items: colleaguesTabs, activeKey: activeTab, onChange: setActiveTab }}
          actions={
            <>
            {/* Left Section: Action Buttons */}
            <div className="header-actions-left">
              <button
                className={`header-action-btn birthday-btn ${colleaguesData?.birthdays?.today?.length > 0 ? 'has-notification' : ''}`}
                onClick={() => setShowBirthdayModal(true)}
                title={colleaguesData?.birthdays?.today?.length > 0
                  ? `Birthday Central - ${colleaguesData.birthdays.today.length} birthday${colleaguesData.birthdays.today.length !== 1 ? 's' : ''} today!`
                  : "Birthday Central"}
              >
                <CRMIcon name="birthday-cake" size="lg" />
                {colleaguesData?.birthdays?.today?.length > 0 && (
                  <span className="notification-badge">{colleaguesData.birthdays.today.length}</span>
                )}
              </button>

              <button
                className={`header-action-btn anniversary-btn ${colleaguesData?.anniversaries?.today?.length > 0 ? 'has-notification' : ''}`}
                onClick={() => setShowAnniversaryModal(true)}
                title={colleaguesData?.anniversaries?.today?.length > 0
                  ? `Anniversary Central - ${colleaguesData.anniversaries.today.length} work anniversary${colleaguesData.anniversaries.today.length !== 1 ? 's' : ''} today!`
                  : "Anniversary Central"}
              >
                <FontAwesomeIcon icon={faBriefcase} size="lg" />
                {colleaguesData?.anniversaries?.today?.length > 0 && (
                  <span className="notification-badge">{colleaguesData.anniversaries.today.length}</span>
                )}
              </button>

              <button
                className="header-action-btn compatibility-btn"
                onClick={() => setShowCompatibilityModal(true)}
                title="Work Style Compatibility"
              >
                <CRMIcon name="handshake" size="lg" />
              </button>
            </div>

            {/* Center Section: Search Bar */}
            <div className="header-search-container">
              <FontAwesomeIcon icon={faSearch} className="header-search-icon" />
              <input
                type="text"
                className="header-search-input"
                placeholder="Search by name or department..."
                value={headerSearchQuery}
                onChange={handleHeaderSearchInput}
              />
              {headerSearchQuery && (
                <button
                  className="header-search-clear"
                  onClick={clearHeaderSearch}
                  aria-label="Clear search"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              )}
            </div>

            {/* Right Section: User Avatar */}
            <div className="header-user-avatar">
              {currentUserAvatar && (
                <img
                  src={currentUserAvatar}
                  alt="Your profile"
                  className="user-avatar-image"
                  onError={(e) => {
                    console.error('❌ Failed to load avatar image:', currentUserAvatar);
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                  onLoad={() => {
                    console.log('✅ Avatar image loaded successfully:', currentUserAvatar);
                  }}
                />
              )}
              <div
                className="user-avatar-initials"
                style={{ display: currentUserAvatar ? 'none' : 'flex' }}
              >
                {colleaguesData?.current_user?.first_name?.[0] || ''}{colleaguesData?.current_user?.last_name?.[0] || ''}
              </div>
            </div>
            </>
          }
        />

        {/* Content Area */}
        <div className="hms-colleagues-content">
          {renderTabContent()}
        </div>

        {/* Hamster Chat Integration */}
        <HamsterFloatingButton />

        {/* Back to Top Button */}
        <BackToTopButton />

        {/* Footer */}
        <Footer />
        </div>
      </Layout>
    </>
  );
};

export default HMSColleagues;